import {
  Box,
  Button,
  Fade,
  FormControl,
  MenuItem,
  Paper,
  Popper,
  Select,
  Typography,
} from '@mui/material'
import { KeyboardArrowDown } from '@mui/icons-material'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'
import CustomSelect from '../Returns/CustomSelect'
import CustomMultiSelect from '../Returns/CustomMultiSelect'
import {
  fetchDashboardFilterOptions,
  fetchDashboardEntitiesByCountries,
  setDashboardFilters,
  resetDashboardFilters,
} from '@/store/slice/dashboardFilterSlice'
import { getYearOptions, getMonthOptions } from '@/utils/dashboardUtils'

// ==================== Constants ====================
const errorTextSx = {
  color: 'var(--forbrand-error)',
  fontSize: 'var(--font-size-xs)',
  mt: 0.5,
  lineHeight: '140%',
}

const DashboardFilter = ({
  open,
  disableCurrencyAndConversion = false,
  anchorEl,
  onClose,
}) => {
  // ==================== Hooks ====================
  const dispatch = useDispatch()
  const store = useStore()
  const options = useSelector(state => state.dashboardFilter.options)
  const optionsLoaded = useSelector(
    state => state.dashboardFilter.optionsLoaded
  )
  const entitiesLoading = useSelector(
    state => state.dashboardFilter.entitiesLoading
  )
  const entitiesError = useSelector(
    state => state.dashboardFilter.entitiesError
  )
  const storeEntityIds = useSelector(state => state.dashboardFilter.entityIds)

  // ==================== Component Helpers (render-critical) ====================
  // Reads the committed selection from the store. A Component Helper (depends on
  // the `store` hook value) declared here — after Hooks, before State — because
  // useState(pickSelection) consumes it during render, so it can't be declared
  // lower or moved to module scope.
  const pickSelection = () => {
    const s = store.getState().dashboardFilter
    return {
      countries: s.countries,
      entityIds: s.entityIds,
      fromMonth: s.fromMonth,
      fromYear: s.fromYear,
      toMonth: s.toMonth,
      toYear: s.toYear,
      currencyCode: s.currencyCode,
      valueConversion: s.valueConversion,
    }
  }

  // ==================== State ====================
  const [filters, setFilters] = useState(pickSelection)
  // Seed the draft with the API-derived defaults ("all" countries/entities)
  // once, when the filter options first finish loading. After that the draft
  // is user-owned and never auto-reset.
  const seededRef = useRef(false)
  const [errors, setErrors] = useState({})
  const [taxPeriodOpen, setTaxPeriodOpen] = useState(false)
  const taxPeriodRef = useRef(null)
  const [tempFromMonth, setTempFromMonth] = useState(null)
  const [tempFromYear, setTempFromYear] = useState(null)
  const [tempToMonth, setTempToMonth] = useState(null)
  const [tempToYear, setTempToYear] = useState(null)
  const [popperWidth, setPopperWidth] = useState(null)

  // ==================== Derived Data ====================
  const entityPlaceholder = (() => {
    if (entitiesLoading) return 'Loading entities...'
    if (entitiesError) return 'Failed to load entities'
    if (!filters.countries?.length) return 'Select a country first'
    if (!options.entityOptions?.length) return 'No entities found'
    return 'Select'
  })()

  // ==================== Effects ====================
  useEffect(() => {
    if (optionsLoaded && !seededRef.current) {
      seededRef.current = true
      setFilters(pickSelection())
    }
  }, [optionsLoaded])

  useEffect(() => {
    if (!optionsLoaded) {
      dispatch(fetchDashboardFilterOptions())
    }
  }, [dispatch, optionsLoaded])

  useEffect(() => {
    if (!anchorEl || !open) return
    const updateWidth = () => {
      setPopperWidth(anchorEl.offsetWidth)
    }
    updateWidth()
    const resizeObserver = new ResizeObserver(updateWidth)
    resizeObserver.observe(anchorEl)
    return () => resizeObserver.disconnect()
  }, [anchorEl, open])

  // Keep the local entity draft in sync with the store whenever the
  // country-driven refetch updates the selection (default "all").
  useEffect(() => {
    setFilters(prev => ({ ...prev, entityIds: storeEntityIds }))
  }, [storeEntityIds])

  // ==================== Handlers ====================
  const handleFilterChange = key => e => {
    const { value } = e.target
    setFilters(prev => ({ ...prev, [key]: value }))
    setErrors(prev => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  // Country selection drives the entity list. On change we clear the local
  // entity draft immediately and refetch entities for the new country codes;
  // the thunk resets/repopulates the entity options and selection in the store.
  const handleCountryChange = e => {
    const { value } = e.target
    setFilters(prev => ({ ...prev, countries: value, entityIds: [] }))
    setErrors(prev => {
      if (!prev.countries) return prev
      const next = { ...prev }
      delete next.countries
      return next
    })
    dispatch(fetchDashboardEntitiesByCountries(value))
  }

  const handleApply = () => {
    const nextErrors = validate()
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return
    dispatch(setDashboardFilters({ ...filters }))
    onClose?.()
  }

  const handleReset = () => {
    // Reset the store to the API-derived defaults. This thunk also refetches
    // the entity list for the full country set, so a prior "no country
    // selected" state can't leave the entity options/selection blank. The
    // country/period/currency defaults land synchronously (pending case), while
    // the refetched entities flow back into the draft via the storeEntityIds
    // effect once the thunk resolves.
    dispatch(resetDashboardFilters())
    // Pull the freshly reset values into the local draft so the popover
    // reflects the reset immediately (the form renders from `filters`).
    setFilters(pickSelection())
    // Clear the transient period picker fields so a reopened picker starts clean.
    setTempFromMonth(null)
    setTempFromYear(null)
    setTempToMonth(null)
    setTempToYear(null)
    setTaxPeriodOpen(false)
    setErrors({})
    onClose?.()
  }

  const handleTaxPeriodClick = () => {
    setTempFromMonth(filters.fromMonth)
    setTempFromYear(filters.fromYear)
    setTempToMonth(filters.toMonth)
    setTempToYear(filters.toYear)
    setTaxPeriodOpen(prev => !prev)
  }

  const handleTaxPeriodApply = () => {
    setFilters(prev => ({
      ...prev,
      fromMonth: tempFromMonth,
      fromYear: tempFromYear,
      toMonth: tempToMonth,
      toYear: tempToYear,
    }))
    setErrors(prev => {
      if (!prev.period) return prev
      const next = { ...prev }
      delete next.period
      return next
    })
    setTaxPeriodOpen(false)
  }

  const handleTaxPeriodCancel = () => {
    setTaxPeriodOpen(false)
  }

  // ==================== Component Helpers ====================
  const validate = () => {
    const nextErrors = {}
    // 1. Country — required
    if (!filters.countries || filters.countries.length === 0) {
      nextErrors.countries = 'Country is required'
    }
    // 4. Currency — required
    if (!filters.currencyCode) {
      nextErrors.currencyCode = 'Currency is required'
    }
    // 5. Value conversion — required
    if (!filters.valueConversion) {
      nextErrors.valueConversion = 'Value conversion is required'
    }
    // 3. Period — optional, but validate the range when provided
    const { fromMonth, fromYear, toMonth, toYear } = filters
    const anyPeriod = fromMonth || fromYear || toMonth || toYear
    const allPeriod = fromMonth && fromYear && toMonth && toYear
    if (anyPeriod && !allPeriod) {
      nextErrors.period = 'Please select a complete period range'
    } else if (allPeriod) {
      const fromDate = new Date(Number(fromYear), Number(fromMonth) - 1, 1)
      const toDate = new Date(Number(toYear), Number(toMonth) - 1, 1)
      if (fromDate > toDate) {
        nextErrors.period = 'The from period cannot be after the to period'
      } else {
        // Maximum range is one year (12 months inclusive), so the "to" period
        // can be at most 11 months after the "from" period.
        const maxDate = new Date(fromDate)
        maxDate.setMonth(maxDate.getMonth() + 11)
        if (toDate > maxDate) {
          nextErrors.period = 'Maximum range between from and to is one year'
        }
      }
    }
    return nextErrors
  }

  const getTaxPeriodDisplay = () => {
    if (
      filters.fromMonth &&
      filters.fromYear &&
      filters.toMonth &&
      filters.toYear
    ) {
      const monthOptions = getMonthOptions()
      const fromLabel = monthOptions.find(
        m => m.value === filters.fromMonth
      )?.label
      const toLabel = monthOptions.find(m => m.value === filters.toMonth)?.label
      return `${fromLabel} ${filters.fromYear} - ${toLabel} ${filters.toYear}`
    }
    return ''
  }

  // ==================== Return ====================
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      transition
      modifiers={[
        {
          name: 'preventOverflow',
          enabled: true,
          options: { altAxis: true, rootBoundary: 'viewport', padding: 0 },
        },
        { name: 'offset', options: { offset: [0, 0] } },
      ]}
      sx={{ zIndex: 1300, width: popperWidth || '100%' }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={200}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              width: '100%',
              borderRadius: 0,
              borderBottom: '1px solid var(--border-subtel)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              bgcolor: 'var(--white-color)',
            }}
          >
            {/* Responsive filter grid — 3 columns on desktop, wraps on smaller screens */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)',
                },
                gap: 2,
                mb: 2,
              }}
            >
              {/* Row 1 */}
              <Box>
                <CustomMultiSelect
                  label="Country"
                  required
                  value={filters.countries}
                  onChange={handleCountryChange}
                  options={options.countryOptions}
                  getOptionLabel={opt => opt.countryName}
                  getOptionValue={opt => opt.countryCode}
                  placeholder="Select"
                  fullWidth
                />
                {errors.countries && (
                  <Typography sx={errorTextSx}>{errors.countries}</Typography>
                )}
              </Box>

              <CustomMultiSelect
                label="Entity"
                value={filters.entityIds}
                onChange={handleFilterChange('entityIds')}
                options={options.entityOptions}
                getOptionLabel={opt => opt.entityName}
                getOptionValue={opt => opt.entityUuid}
                placeholder={entityPlaceholder}
                disabled={
                  entitiesLoading ||
                  entitiesError ||
                  !filters.countries?.length ||
                  !options.entityOptions?.length
                }
                fullWidth
              />

              {/* Period — Month/Year Range with Popper */}
              <Box>
                <Typography
                  sx={{
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 500,
                    color: 'var(--forbrand-primary)',
                    mb: 1,
                    lineHeight: '140%',
                  }}
                >
                  Period
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={getTaxPeriodDisplay() || ''}
                    open={false}
                    onOpen={handleTaxPeriodClick}
                    IconComponent={KeyboardArrowDown}
                    displayEmpty
                    renderValue={() => {
                      const display = getTaxPeriodDisplay()
                      if (!display) {
                        return <span style={{ color: '#999' }}>Select</span>
                      }
                      return display
                    }}
                    ref={taxPeriodRef}
                    sx={{
                      backgroundColor: 'var(--white-color)',
                      fontSize: 'var(--font-size-sm)',
                      height: '48px',
                      width: '100%',
                      borderRadius: 'unset',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--border-subtel)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--border-strong)',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'var(--forbrand-color)',
                      },
                      '& .MuiSelect-select': {
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--forbrand-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                      '& .MuiSelect-icon': {
                        color: 'var(--forbrand-secondary)',
                        fontSize: '20px',
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <span style={{ color: '#999' }}>Select</span>
                    </MenuItem>
                  </Select>
                </FormControl>
                <Popper
                  open={taxPeriodOpen}
                  anchorEl={taxPeriodRef.current}
                  placement="bottom-start"
                  transition
                  sx={{ zIndex: 1400 }}
                >
                  {({ TransitionProps: innerTransitionProps }) => (
                    <Fade {...innerTransitionProps} timeout={200}>
                      <Paper
                        elevation={3}
                        sx={{ p: 2, mt: 0.5, minWidth: 340 }}
                      >
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <CustomSelect
                            label="From month"
                            value={tempFromMonth}
                            onChange={e => setTempFromMonth(e.target.value)}
                            options={getMonthOptions()}
                            getOptionLabel={opt => opt.label}
                            getOptionValue={opt => opt.value}
                            placeholder="Select"
                            MenuProps={{
                              sx: { zIndex: 1500 },
                              PaperProps: {
                                sx: {
                                  boxShadow:
                                    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                                  border: '1px solid var(--border-strong)',
                                  mt: 0.5,
                                  maxHeight: '250px',
                                },
                              },
                            }}
                          />
                          <CustomSelect
                            label="From year"
                            value={tempFromYear}
                            onChange={e => setTempFromYear(e.target.value)}
                            options={getYearOptions()}
                            getOptionLabel={opt => opt.label}
                            getOptionValue={opt => opt.value}
                            placeholder="Select"
                            MenuProps={{
                              sx: { zIndex: 1500 },
                              PaperProps: {
                                sx: {
                                  boxShadow:
                                    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                                  border: '1px solid var(--border-strong)',
                                  mt: 0.5,
                                  maxHeight: '250px',
                                },
                              },
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <CustomSelect
                            label="To month"
                            value={tempToMonth}
                            onChange={e => setTempToMonth(e.target.value)}
                            options={getMonthOptions()}
                            getOptionLabel={opt => opt.label}
                            getOptionValue={opt => opt.value}
                            placeholder="Select"
                            MenuProps={{
                              sx: { zIndex: 1500 },
                              PaperProps: {
                                sx: {
                                  boxShadow:
                                    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                                  border: '1px solid var(--border-strong)',
                                  mt: 0.5,
                                  maxHeight: '250px',
                                },
                              },
                            }}
                          />
                          <CustomSelect
                            label="To year"
                            value={tempToYear}
                            onChange={e => setTempToYear(e.target.value)}
                            options={getYearOptions()}
                            getOptionLabel={opt => opt.label}
                            getOptionValue={opt => opt.value}
                            placeholder="Select"
                            MenuProps={{
                              sx: { zIndex: 1500 },
                              PaperProps: {
                                sx: {
                                  boxShadow:
                                    '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
                                  border: '1px solid var(--border-strong)',
                                  mt: 0.5,
                                  maxHeight: '250px',
                                },
                              },
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                          }}
                        >
                          <Button
                            onClick={handleTaxPeriodCancel}
                            variant="text"
                            sx={{
                              textTransform: 'none',
                              fontSize: 'var(--font-size-sm)',
                              color: 'var(--forbrand-primary)',
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            disabled={
                              !tempFromMonth ||
                              !tempFromYear ||
                              !tempToMonth ||
                              !tempToYear
                            }
                            onClick={handleTaxPeriodApply}
                            variant="contained"
                            sx={{
                              textTransform: 'none',
                              fontSize: 'var(--font-size-sm)',
                              backgroundColor: 'var(--forbrand-bg-variant)',
                              color: 'var(--forbrand-brand-varient)',
                              boxShadow: 'none',
                            }}
                          >
                            Apply
                          </Button>
                        </Box>
                      </Paper>
                    </Fade>
                  )}
                </Popper>
                {errors.period && (
                  <Typography sx={errorTextSx}>{errors.period}</Typography>
                )}
              </Box>

              {/* Row 2 */}
              <Box>
                <CustomSelect
                  label="Currency"
                  required
                  value={filters.currencyCode}
                  onChange={handleFilterChange('currencyCode')}
                  options={options.currencyOptions}
                  getOptionLabel={opt => opt.currencyCode}
                  getOptionValue={opt => opt.currencyCode}
                  placeholder="Select"
                  disabled={disableCurrencyAndConversion}
                  fullWidth
                />
                {errors.currencyCode && (
                  <Typography sx={errorTextSx}>
                    {errors.currencyCode}
                  </Typography>
                )}
              </Box>

              <Box>
                <CustomSelect
                  label="Value conversion"
                  required
                  value={filters.valueConversion}
                  onChange={handleFilterChange('valueConversion')}
                  options={options.valueConversionOptions}
                  getOptionLabel={opt => opt.conversionCode}
                  getOptionValue={opt => opt.conversionValue}
                  placeholder="Select"
                  disabled={disableCurrencyAndConversion}
                  fullWidth
                />
                {errors.valueConversion && (
                  <Typography sx={errorTextSx}>
                    {errors.valueConversion}
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                onClick={handleReset}
                variant="text"
                sx={{
                  textTransform: 'none',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  color: 'var(--forbrand-primary)',
                }}
              >
                Reset All
              </Button>
              <Button
                onClick={handleApply}
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 500,
                  backgroundColor: 'var(--forbrand-bg-variant)',
                  color: 'var(--forbrand-brand-varient)',
                  boxShadow: 'none',
                  px: 3,
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Paper>
        </Fade>
      )}
    </Popper>
  )
}

export default DashboardFilter
